import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  Grid,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Key as KeyIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  SmartToy as AiIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, {
  ReactFlowProvider,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Node,
  Edge,
  Connection,
  useReactFlow,
  Panel,
  NodeChange,
  EdgeChange,
  getBezierPath,
  getSmoothStepPath,
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Close as CloseIcon } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';

import { NodePalette } from '../components/workflows/NodePalette';
import { LibraryDrawer } from '../components/workflows/LibraryDrawer';
import { NodeTypes } from '../components/workflows/NodeTypes';
import { ImportedNodeComponent } from '../components/node-import/ImportedNodeComponent';
import { NodeInspector } from '../components/workflows/NodeInspector';
import { WorkflowAIChat } from '../components/workflows/WorkflowAIChat';
import { workflowService } from '../services/workflow.service';
import { executionService } from '../services/execution.service';
import { nodeImportService } from '../services/nodeImport.service';
import { Workflow } from '../types/workflow.types';
import { Execution, NodeExecution } from '../types/workflow.types';
import { useAuth } from '../contexts/AuthContext';

const DeletableEdge: React.FC<EdgeProps> = ({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
  style, markerEnd, label, labelStyle, data, selected,
}) => {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX, sourceY, sourcePosition,
    targetX, targetY, targetPosition,
  });

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          strokeWidth: selected ? 3 : 2,
          cursor: 'pointer',
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {label && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 'bold',
                ...(labelStyle as any),
                background: 'rgba(255,255,255,0.85)',
                padding: '1px 6px',
                borderRadius: 4,
              }}
            >
              {label}
            </span>
          )}
          {selected && (
            <button
              title="Delete connection"
              className="edgebutton"
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: '1.5px solid #f44336',
                background: '#fff',
                color: '#f44336',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                fontSize: 14,
                fontWeight: 'bold',
                lineHeight: 1,
                boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                const evt = new CustomEvent('delete-edge', { detail: { edgeId: id } });
                window.dispatchEvent(evt);
              }}
            >
              ×
            </button>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const edgeTypes = { deletable: DeletableEdge };

const nodeTypes = {
  trigger: NodeTypes.TriggerNode,
  action: NodeTypes.ActionNode,
  core: NodeTypes.CoreNode,
  imported: ImportedNodeComponent,
};

const WorkflowCanvas: React.FC<{
  nodes: Node[];
  edges: Edge[];
  onNodesChange: any;
  onEdgesChange: any;
  onConnect: (connection: Connection) => void;
  onNodeAdd: (node: Node) => void;
  onNodeSelect: (nodeId: string | null) => void;
  onEdgeSelect: (edgeId: string | null) => void;
  onNodeDragCommit: () => void;
}> = ({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onNodeAdd, onNodeSelect, onEdgeSelect, onNodeDragCommit }) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowInstance || !reactFlowBounds) return;

      try {
        const data = JSON.parse(event.dataTransfer.getData('application/reactflow'));
        const position = reactFlowInstance.project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });

        const newNode: Node = {
          id: `${data.type}_${Date.now()}`,
          type: data.nodeType || 'trigger',
          position,
          data: {
            label: data.name,
            nodeType: data.type,
            parameters: data.parameters || {},
            description: data.description,
          },
        };

        onNodeAdd(newNode);
      } catch (e) {
        console.error('Error dropping node:', e);
      }
    },
    [reactFlowInstance, onNodeAdd]
  );

  return (
    <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={setReactFlowInstance}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={(_, node) => { onNodeSelect(node.id); onEdgeSelect(null); }}
        onEdgeClick={(_, edge) => { onEdgeSelect(edge.id); onNodeSelect(null); }}
        onPaneClick={() => { onNodeSelect(null); onEdgeSelect(null); }}
        onNodeDragStop={onNodeDragCommit}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        edgesFocusable
        fitView
        fitViewOptions={{ maxZoom: 1 }}
      >
        <Controls />
        <MiniMap />
        <Background gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

export const WorkflowEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const { user } = useAuth();

  const [workflow, setWorkflow] = useState<Partial<Workflow>>({
    name: '',
    description: '',
    is_published: true,
  });
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  const [executionId, setExecutionId] = useState<string | null>(null);
  const [execution, setExecution] = useState<Execution | null>(null);
  const [nodeExecutions, setNodeExecutions] = useState<NodeExecution[]>([]);
  const [executionPanelOpen, setExecutionPanelOpen] = useState(false);
  const [executionModalOpen, setExecutionModalOpen] = useState(false);
  const [executeInputJson, setExecuteInputJson] = useState<string>('{\n  "body": {\n    "message": "Test payload"\n  }\n}');
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [graphDirty, setGraphDirty] = useState(false);
  const [metaDirty, setMetaDirty] = useState(false);
  const [aiSidebarOpen, setAiSidebarOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const removedNodeIdsRef = useRef<Set<string>>(new Set());
  const removedConnectionIdsRef = useRef<Set<string>>(new Set());
  const createdIdRef = useRef<string | null>(null);

  const isUUID = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

  const hasMeaningfulNodeChanges = (changes: NodeChange[]) => {
    return changes.some((ch: any) => ch?.type === 'add' || ch?.type === 'remove');
  };

  const hasMeaningfulEdgeChanges = (changes: EdgeChange[]) => {
    return changes.some((ch: any) => ch?.type === 'add' || ch?.type === 'remove');
  };

  const toArray = (value: any): any[] => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.results)) return value.results;
    // Some APIs may wrap again as { data: { results: [] } }
    if (value?.data && Array.isArray(value.data.results)) return value.data.results;
    return [];
  };

  const selectedNode = nodes.find((n: any) => n.id === selectedNodeId) || null;

  const hasPerm = (perm: string) => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return Array.isArray((user as any).permissions) && (user as any).permissions.includes(perm);
  };

  const canCreate = hasPerm('workflow.create');
  const canEdit = hasPerm('workflow.edit');
  const canExecute = hasPerm('workflow.execute');
  const canDelete = hasPerm('workflow.delete');
  const canModify = isNew ? canCreate : canEdit;
  const isSystemAdmin = !!user && user.role === 'system_admin';

  const handleDeleteSelectedEdge = () => {
    if (!canModify || !selectedEdgeId) return;
    if (isUUID(selectedEdgeId)) {
      removedConnectionIdsRef.current.add(selectedEdgeId);
    }
    setGraphDirty(true);
    setEdges((eds: any[]) => eds.filter((e) => e.id !== selectedEdgeId));
    setSelectedEdgeId(null);
  };

  // Delete key support (nodes and edges)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!canModify) return;
      // Don't delete while typing in inputs
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (target as any)?.isContentEditable) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedEdgeId) {
          e.preventDefault();
          handleDeleteSelectedEdge();
        } else if (selectedNodeId) {
          e.preventDefault();
          handleDeleteSelectedNode();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, selectedEdgeId, nodes, edges, canModify]);

  // Handle delete-edge events from the custom edge button
  useEffect(() => {
    const onDeleteEdge = (e: Event) => {
      if (!canModify) return;
      const edgeId = (e as CustomEvent).detail?.edgeId;
      if (!edgeId) return;
      if (isUUID(edgeId)) {
        removedConnectionIdsRef.current.add(edgeId);
      }
      setGraphDirty(true);
      setEdges((eds: any[]) => eds.filter((ed) => ed.id !== edgeId));
      setSelectedEdgeId(null);
    };
    window.addEventListener('delete-edge', onDeleteEdge);
    return () => window.removeEventListener('delete-edge', onDeleteEdge);
  }, [canModify, setEdges]);

  useEffect(() => {
    if (id) {
      loadWorkflow();
    } else {
      setLoading(false);
    }
  }, [id]);

  // Poll execution status when panel is open
  useEffect(() => {
    let timer: any;
    const tick = async () => {
      if (!executionId) return;
      try {
        const ex = await executionService.getExecution(executionId);
        setExecution(ex);
        const nx = await executionService.getNodeExecutions(executionId);
        setNodeExecutions(nx);

        if (ex.status === 'running' || ex.status === 'pending') {
          timer = setTimeout(tick, 1500);
        }
      } catch {
        // Ignore polling errors
      }
    };

    if (executionPanelOpen && executionId) {
      tick();
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [executionPanelOpen, executionId]);

  const loadWorkflow = async () => {
    try {
      setLoading(true);
      const workflowData = await workflowService.getWorkflow(id!);
      setWorkflow(workflowData);

      // Load imported node definitions so we can render them with the richer ImportedNodeComponent style
      // (instead of falling back to the plain core/action/trigger nodes).
      let importedByName: Record<string, any> = {};
      try {
        const imported = await nodeImportService.getActiveNodes();
        importedByName = (imported.nodes || []).reduce((acc: any, n: any) => {
          if (n?.name) acc[n.name] = n;
          return acc;
        }, {});
      } catch {
        // ignore - workflow should still load
      }

      // Convert workflow nodes to React Flow nodes
      const flowNodes: Node[] = (workflowData.nodes || []).map((node: any) => {
        const rawType = String(node.node_type || node.nodeType || '');
        // Mirror of backend WorkflowService._N8N_TO_NATIVE_TYPE so old workflows
        // (imported BEFORE the backend translator existed) still land on the
        // native UI block — they were stored with literal `n8n-nodes-base.*`
        // types and would otherwise fall back to the generic "imported" form
        // even though we have a dedicated UI for them now.
        const N8N_TO_NATIVE: Record<string, string> = {
          'n8n-nodes-base.webhook': 'trigger.webhook',
          'n8n-nodes-base.formTrigger': 'trigger.webhook',
          '@n8n/n8n-nodes-langchain.chatTrigger': 'trigger.webhook',
          'n8n-nodes-base.scheduleTrigger': 'trigger.schedule',
          'n8n-nodes-base.cron': 'trigger.schedule',
          'n8n-nodes-base.manualTrigger': 'trigger.manual',
          'n8n-nodes-base.set': 'core.set',
          'n8n-nodes-base.if': 'core.if',
          'n8n-nodes-base.switch': 'core.switch',
          'n8n-nodes-base.code': 'core.code',
          'n8n-nodes-base.function': 'core.code',
          'n8n-nodes-base.functionItem': 'core.code',
          'n8n-nodes-base.wait': 'core.delay',
          'n8n-nodes-base.httpRequest': 'action.http',
          'n8n-nodes-base.respondToWebhook': 'action.webhook.respond',
          'n8n-nodes-base.emailSend': 'action.smtp',
          'n8n-nodes-base.gmail': 'action.gmail',
          'n8n-nodes-base.emailReadImap': 'action.imap',
          'n8n-nodes-base.postgres': 'action.postgresql',
          'n8n-nodes-base.postgresTool': 'action.postgresql',
          'n8n-nodes-base.supabase': 'action.supabase',
          '@n8n/n8n-nodes-langchain.vectorStorePGVector': 'action.qdrant',
          'n8n-nodes-base.slack': 'action.slack',
          'n8n-nodes-base.discord': 'action.discord',
          'n8n-nodes-base.microsoftTeams': 'action.microsoft_teams',
          'n8n-nodes-base.zoom': 'action.zoom',
          'n8n-nodes-base.googleSheets': 'action.google_sheet',
          'n8n-nodes-base.googleDrive': 'action.google_drive',
          'n8n-nodes-base.googleCalendar': 'action.google_calendar',
          'n8n-nodes-base.microsoftOutlook': 'action.microsoft365',
          'n8n-nodes-base.microsoftGraphSecurity': 'action.microsoft365',
          'n8n-nodes-base.notion': 'action.notion',
          'n8n-nodes-base.airtable': 'action.airtable',
          'n8n-nodes-base.dropbox': 'action.dropbox',
          'n8n-nodes-base.calCom': 'action.cal_com',
          'n8n-nodes-base.jira': 'action.jira',
          'n8n-nodes-base.confluence': 'action.confluence',
          'n8n-nodes-base.asana': 'action.asana',
          'n8n-nodes-base.trello': 'action.trello',
          'n8n-nodes-base.hubspot': 'action.hubspot',
          'n8n-nodes-base.shopify': 'action.shopify',
          'n8n-nodes-base.zohoCrm': 'action.zoho_crm',
          'n8n-nodes-base.pipedrive': 'action.pipedrive',
          'n8n-nodes-base.insightly': 'action.insightly',
          'n8n-nodes-base.odoo': 'action.odoo',
          'n8n-nodes-base.erpnext': 'action.erpnext',
          'n8n-nodes-base.netsuite': 'action.netsuite',
          'n8n-nodes-base.quickbooks': 'action.quickbooks',
          'n8n-nodes-base.sap': 'action.sap',
          'n8n-nodes-base.xero': 'action.xero',
          'n8n-nodes-base.myob': 'action.myob',
          'n8n-nodes-base.mailchimp': 'action.mailchimp',
          '@n8n/n8n-nodes-langchain.lmChatOpenAi': 'action.llm.chat',
          '@n8n/n8n-nodes-langchain.openAi': 'action.llm.chat',
        };
        const nativeMappedType = N8N_TO_NATIVE[rawType] || '';
        // Effective node type the editor / inspector should use. Falls back
        // to the original stored value when no native equivalent exists.
        const nt = nativeMappedType || rawType;
        // A node is considered "natively typed" when its stored type already
        // belongs to ODA's own namespace (``core.*`` / ``action.*`` /
        // ``trigger.*``). These ALWAYS use the dedicated native renderer,
        // even if the imported-nodes registry happens to contain a row with
        // the same name (e.g. an n8n import that erroneously registered
        // ``core.if`` as a custom imported type — that would otherwise
        // shadow the native UI and strip the True/False handles off
        // ``core.if`` nodes).
        const isNativeShape = /^(core|action|trigger)\./.test(nt) || /^(core|action|trigger)\./.test(rawType);
        const importedMeta = importedByName[nt] || importedByName[rawType];
        // Only flag a node as "imported" (which switches the canvas to the
        // generic ImportedNodeComponent + generic JSON inspector form) when
        // the type genuinely has no native equivalent. Nodes mapped above —
        // or already in the native ``core.*`` / ``action.*`` / ``trigger.*``
        // namespace — get the dedicated native UI just like a freshly
        // dragged palette node.
        const treatAsImported =
          !nativeMappedType &&
          !isNativeShape &&
          (importedMeta || rawType.startsWith('n8n-') || rawType.startsWith('@n8n/'));

        return {
          id: node.id || `${rawType}_${Date.now()}`,
          type: treatAsImported ? 'imported' : ((nt || 'action').split('.')[0] || 'action'),
          position: { x: node.position_x || 0, y: node.position_y || 0 },
          data: {
            label: importedMeta?.display_name || node.name,
            workflowId: String(node.workflow || workflowData.id || ''),
            nodeType: nt,
            webhookSecret: node.webhook_secret || '',
            parameters: node.parameters || {},
            description: node.description,
            ...(treatAsImported
              ? {
                  isImported: true,
                  validationErrors: importedMeta?.validation_errors || [],
                  uiSchema: importedMeta?.ui_schema || null,
                  parametersSchema: importedMeta?.parameters_schema || null,
                  importedProperties:
                    importedMeta?.properties ||
                    importedMeta?.original_n8n_data?.properties ||
                    [],
                }
              : {}),
          },
        };
      });

      // Build a quick lookup of node types by id for edge enrichment.
      // Use the same effective (post-mapping) node type the canvas/inspector
      // uses, so legacy `n8n-nodes-base.if` nodes still get True/False edge
      // labels after the auto-remap to `core.if` above.
      const nodeTypeById: Record<string, string> = {};
      flowNodes.forEach((fn: any) => {
        if (fn?.id) nodeTypeById[fn.id] = String(fn?.data?.nodeType || '');
      });

      // Convert workflow connections to React Flow edges
      const flowEdges: Edge[] = (workflowData.connections || []).map((conn: any) => {
        const condition = { ...(conn.condition || {}) };
        let branch: string | undefined = condition.branch;

        // Auto-assign "true" branch to legacy connections from If nodes that
        // were created before the True/False handle UI was deployed.
        const sourceType = nodeTypeById[conn.source_node || conn.source] || '';
        if (!branch && sourceType === 'core.if') {
          branch = 'true';
          condition.branch = 'true';
        }

        let edgeColor = '#1976d2';
        let edgeLabel: string | undefined;
        const switchColors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63'];

        if (sourceType === 'core.if') {
          edgeColor = branch === 'true' ? '#4caf50' : branch === 'false' ? '#f44336' : '#1976d2';
          edgeLabel = branch === 'true' ? 'True' : branch === 'false' ? 'False' : undefined;
        } else if (sourceType === 'core.switch') {
          if (branch === 'default') {
            edgeColor = '#757575';
            edgeLabel = 'Default';
          } else if (branch?.startsWith('case-')) {
            const idx = Number(branch.replace('case-', ''));
            edgeColor = switchColors[idx % switchColors.length];
            edgeLabel = `Case ${idx}`;
          }
        }

        return {
          id: conn.id || `${conn.source}_${conn.target}`,
          source: conn.source_node || conn.source,
          target: conn.target_node || conn.target,
          ...(branch ? { sourceHandle: branch } : {}),
          type: 'deletable',
          animated: true,
          label: edgeLabel,
          labelStyle: branch ? { fill: edgeColor, fontWeight: 'bold', fontSize: 11 } : undefined,
          style: { stroke: edgeColor },
          data: { condition },
        };
      });

      setNodes(flowNodes);
      setEdges(flowEdges);
      setSelectedNodeId(null);
      setGraphDirty(false);
      removedNodeIdsRef.current.clear();
      removedConnectionIdsRef.current.clear();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load workflow');
      enqueueSnackbar('Failed to load workflow', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onConnect = useCallback((params: Connection) => {
    if (!canModify) return;
    setGraphDirty(true);
    setEdges((eds) => {
      const sourceNode = (nodes as any[]).find((n) => n.id === params.source);
      const sourceType = sourceNode?.data?.nodeType || '';
      const handle = params.sourceHandle ?? undefined;

      let branch: string | undefined;
      let edgeLabel: string | undefined;

      if (sourceType === 'core.if') {
        branch = handle;
        edgeLabel = branch === 'true' ? 'True' : branch === 'false' ? 'False' : undefined;
      } else if (sourceType === 'core.switch') {
        branch = handle;
        if (handle === 'default') {
          edgeLabel = 'Default';
        } else if (handle?.startsWith('case-')) {
          const idx = handle.replace('case-', '');
          const cases = sourceNode?.data?.parameters?.cases || [];
          const caseVal = cases[Number(idx)]?.value;
          edgeLabel = caseVal ? `Case: ${caseVal}` : `Case ${idx}`;
        }
      }

      const switchColors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63'];
      let edgeColor = '#1976d2';
      if (sourceType === 'core.if') {
        edgeColor = branch === 'true' ? '#4caf50' : branch === 'false' ? '#f44336' : '#1976d2';
      } else if (sourceType === 'core.switch') {
        if (handle === 'default') {
          edgeColor = '#757575';
        } else if (handle?.startsWith('case-')) {
          const idx = Number(handle.replace('case-', ''));
          edgeColor = switchColors[idx % switchColors.length];
        }
      }

      const condition: Record<string, any> = {};
      if (branch) condition.branch = branch;

      return addEdge(
        {
          ...params,
          type: 'deletable',
          animated: true,
          label: edgeLabel,
          labelStyle: branch ? { fill: edgeColor, fontWeight: 'bold', fontSize: 11 } : undefined,
          style: { stroke: edgeColor },
          data: { condition },
        },
        eds
      );
    });
  }, [setEdges, canModify, nodes]);

  const handleNodeAdd = useCallback((node: Node) => {
    if (!canModify) return;
    setGraphDirty(true);
    const currentWorkflowId = id || createdIdRef.current || '';
    const enriched: Node = currentWorkflowId
      ? {
          ...node,
          data: {
            ...(node.data || {}),
            workflowId: String((node.data as any)?.workflowId || currentWorkflowId),
          },
        }
      : node;
    setNodes((nds) => [...nds, enriched]);
  }, [setNodes, canModify, id]);

  const handleDeleteSelectedNode = () => {
    if (!canModify) return;
    if (!selectedNodeId) return;
    if (isUUID(selectedNodeId)) {
      removedNodeIdsRef.current.add(selectedNodeId);
    }
    setGraphDirty(true);
    setNodes((nds: any[]) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds: any[]) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
  };

  const handleSelectedNodeChange = (update: { label?: string; parameters?: any }) => {
    if (!canModify) return;
    if (!selectedNodeId) return;
    setGraphDirty(true);
    setNodes((nds: any[]) =>
      nds.map((n) => {
        if (n.id !== selectedNodeId) return n;
        return {
          ...n,
          data: {
            ...n.data,
            ...(update.label !== undefined ? { label: update.label } : {}),
            ...(update.parameters !== undefined ? { parameters: update.parameters } : {}),
          },
        };
      })
    );
  };

  const applyAIChanges = useCallback(
    (changes: { add_nodes: any[]; add_connections: any[]; remove_nodes: string[]; update_nodes: any[] }) => {
      const tempIdMap = new Map<string, string>();

      if (changes.remove_nodes?.length) {
        const removeSet = new Set(changes.remove_nodes);
        setNodes((nds) => nds.filter((n) => !removeSet.has(n.id)));
        setEdges((eds) => eds.filter((e) => !removeSet.has(e.source) && !removeSet.has(e.target)));
        for (const rid of changes.remove_nodes) {
          if (isUUID(rid)) removedNodeIdsRef.current.add(rid);
        }
      }

      if (changes.update_nodes?.length) {
        setNodes((nds) =>
          nds.map((n) => {
            const upd = changes.update_nodes.find((u: any) => u.id === n.id);
            if (!upd) return n;
            return {
              ...n,
              data: {
                ...n.data,
                ...(upd.name ? { label: upd.name } : {}),
                ...(upd.parameters ? { parameters: { ...n.data?.parameters, ...upd.parameters } } : {}),
              },
            };
          })
        );
      }

      if (changes.add_nodes?.length) {
        const newFlowNodes: Node[] = changes.add_nodes.map((an: any) => {
          const localId = `${an.node_type || 'node'}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
          if (an.temp_id) tempIdMap.set(an.temp_id, localId);
          const category = (an.node_type || 'action').split('.')[0] || 'action';
          return {
            id: localId,
            type: category,
            position: { x: an.position_x || 400, y: an.position_y || 300 },
            data: {
              label: an.name || an.node_type || 'New Node',
              nodeType: an.node_type,
              parameters: an.parameters || {},
            },
          };
        });
        setNodes((nds) => [...nds, ...newFlowNodes]);
      }

      if (changes.add_connections?.length) {
        const newEdges: Edge[] = changes.add_connections.map((ac: any, i: number) => {
          const source = tempIdMap.get(ac.source) || ac.source;
          const target = tempIdMap.get(ac.target) || ac.target;
          return {
            id: `ai_edge_${Date.now()}_${i}`,
            source,
            target,
            type: 'deletable',
            animated: true,
            data: { condition: {} },
          };
        });
        setEdges((eds) => [...eds, ...newEdges]);
      }

      setGraphDirty(true);
    },
    [setNodes, setEdges]
  );

  const handleAIUndo = useCallback(
    (snapshot: { nodes: Node[]; edges: Edge[] }) => {
      setNodes(snapshot.nodes);
      setEdges(snapshot.edges);
      setGraphDirty(true);
    },
    [setNodes, setEdges]
  );

  const handleSave = async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    try {
      if (!canModify) {
        if (!silent) {
          enqueueSnackbar('You do not have permission to edit workflows.', { variant: 'error' });
        }
        return;
      }
      // Force any focused editor (multiline TextField, JSON editor, Code box,
      // etc.) to commit its pending text. Many fields in NodeInspector buffer
      // local state and only push to node parameters on `onBlur` -- without this
      // a user clicking Save while still typing would lose their last edit.
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        try {
          document.activeElement.blur();
        } catch {
          // ignore
        }
        // Yield a tick so the resulting onBlur -> setNodes state update flushes
        // before we read `nodes` below.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
      setSaving(true);
      setError(null);

      // 1) Create/Update workflow shell (name/description/settings)
      const workflowShell: Partial<Workflow> = {
        name: workflow.name as any,
        description: workflow.description as any,
        is_active: (workflow as any).is_active ?? true,
        is_published: (workflow as any).is_published ?? true,
        settings: (workflow as any).settings ?? {},
      } as any;

      let workflowId = id || createdIdRef.current;
      if (workflowId) {
        await workflowService.updateWorkflow(workflowId, workflowShell);
      } else {
        const created = await workflowService.createWorkflow(workflowShell);
        workflowId = created.id;
        createdIdRef.current = workflowId;
        enqueueSnackbar('Workflow created successfully', { variant: 'success' });
      }

      // NOTE: we used to early-return here when `!graphDirty` to skip
      // re-persisting nodes/connections, but that caused an annoying class of
      // bugs where edits to a node's parameters (e.g. tweaking code in the
      // Code node) would be silently discarded because something failed to
      // flip `graphDirty` to true. The save would show "saved successfully"
      // yet no PATCH was issued. Always persist nodes -- the per-node PATCH is
      // cheap and idempotent, and connections are only created/deleted based
      // on the diff against the server below.

      // 2) Persist nodes
      const [serverNodes, serverConnections] = await Promise.all([
        workflowService.getNodes(workflowId!),
        workflowService.getConnections(workflowId!),
      ]);

      const serverNodesList = toArray(serverNodes);
      const serverConnectionsList = toArray(serverConnections);
      const currentNodeIds = new Set((nodes as any[]).map((n) => String(n.id)));

      const serverNodeIds = new Set(serverNodesList.map((n: any) => n.id));
      const keepNodeIds = new Set<string>();
      const idMap = new Map<string, string>(); // tempId -> uuid
      const createdNodeMeta = new Map<string, { id: string; webhookSecret?: string; workflowId?: string }>();

      const isNotFound = (err: any) => err?.response?.status === 404;

      // Create/update nodes (and build mapping for temp ids).
      //
      // IMPORTANT: do NOT short-circuit "isUUID && !serverNodeIds.has(id)"
      // by skipping. The server list can be paginated (DRF default
      // PAGE_SIZE=20) or filtered for any number of reasons; if a UUID-id
      // node exists locally we should always try to PATCH it. If the row
      // truly doesn't exist on the server (404) we fall back to CREATE so
      // the edit isn't silently lost. This was the cause of the long-
      // standing "edits to existing nodes don't save (but recreate works)"
      // bug -- with 21+ nodes the rows beyond the first page were skipped.
      for (const n of nodes as any[]) {
        const payload = {
          name: n.data?.label || 'Node',
          node_type: n.data?.nodeType,
          parameters: n.data?.parameters || {},
          position_x: Math.round(n.position?.x ?? 0),
          position_y: Math.round(n.position?.y ?? 0),
        };

        const recreateAsNew = async () => {
          const createdNode = await workflowService.createNode(workflowId!, payload as any);
          const createdNodeAny = createdNode as any;
          idMap.set(n.id, createdNode.id);
          createdNodeMeta.set(n.id, {
            id: createdNode.id,
            webhookSecret: createdNodeAny.webhook_secret || '',
            workflowId: String(createdNodeAny.workflow || workflowId || ''),
          });
          keepNodeIds.add(createdNode.id);
        };

        if (isUUID(n.id)) {
          keepNodeIds.add(n.id);
          try {
            await workflowService.updateNode(workflowId!, n.id, payload as any);
          } catch (err: any) {
            if (isNotFound(err)) {
              // Node exists locally but no longer on server (deleted by
              // another tab, restored from undo, etc). Recreate it so the
              // edit is preserved.
              keepNodeIds.delete(n.id);
              await recreateAsNew();
            } else {
              throw err;
            }
          }
        } else {
          await recreateAsNew();
        }
      }

      // Delete only nodes explicitly removed in editor (safe against accidental wipe).
      for (const removedId of Array.from(removedNodeIdsRef.current)) {
        // Ignore stale remove events: never delete if node still exists in current graph.
        if (currentNodeIds.has(String(removedId))) continue;
        try {
          await workflowService.deleteNode(workflowId!, removedId);
        } catch (err: any) {
          // If the node was already deleted (race / previous save), ignore.
          if (!isNotFound(err)) throw err;
        }
      }

      // Apply idMap to local nodes + edges
      if (idMap.size > 0) {
        setNodes((nds: any[]) =>
          nds.map((n) => {
            if (!idMap.has(n.id)) return n;
            const meta = createdNodeMeta.get(n.id);
            return {
              ...n,
              id: meta?.id || idMap.get(n.id),
              data: {
                ...n.data,
                workflowId: meta?.workflowId || String(workflowId || ''),
                webhookSecret: meta?.webhookSecret || n.data?.webhookSecret || '',
              },
            };
          })
        );
        setEdges((eds: any[]) =>
          eds.map((e) => ({
            ...e,
            source: idMap.get(e.source) || e.source,
            target: idMap.get(e.target) || e.target,
          }))
        );
      }

      // 3) Persist connections (safe upsert; delete only explicitly removed)
      const serverConnectionIds = new Set(serverConnectionsList.map((c: any) => c.id as string));
      const currentEdgeIds = new Set((edges as any[]).filter((e) => isUUID(e.id)).map((e) => String(e.id)));

      // Need the latest node ids for edges
      const currentNodes = (idMap.size > 0)
        ? (nodes as any[]).map((n) => (idMap.has(n.id) ? { ...n, id: idMap.get(n.id) } : n))
        : (nodes as any[]);

      const validNodeIds = new Set(currentNodes.map((n) => n.id));
      const newEdges: any[] = [];
      for (const e of edges as any[]) {
        const source = idMap.get(e.source) || e.source;
        const target = idMap.get(e.target) || e.target;
        if (!validNodeIds.has(source) || !validNodeIds.has(target)) continue;

        // Preserve existing connection condition (critical for imported n8n workflows).
        // If missing, default to {} (plain main-path edge).
        const condition = (e as any)?.data?.condition || {};
        const connBody = {
          source_node: source,
          target_node: target,
          condition,
        };
        let savedConn: any;
        if (isUUID(e.id)) {
          // Same fix as nodes above: don't trust serverConnectionIds (it
          // can be paginated). Try PATCH first, fall back to CREATE on 404.
          try {
            savedConn = await workflowService.updateConnection(
              workflowId!,
              e.id,
              connBody as any,
            );
          } catch (err: any) {
            if (isNotFound(err)) {
              savedConn = await workflowService.createConnection(workflowId!, connBody as any);
            } else {
              throw err;
            }
          }
        } else {
          savedConn = await workflowService.createConnection(workflowId!, connBody as any);
        }
        newEdges.push({
          ...e,
          id: savedConn.id,
          source,
          target,
          data: {
            ...(e as any).data,
            condition: savedConn.condition || condition,
          },
        });
      }
      for (const removedConnectionId of Array.from(removedConnectionIdsRef.current)) {
        // Ignore stale remove events: never delete if edge still exists in current graph.
        if (currentEdgeIds.has(String(removedConnectionId))) continue;
        try {
          await workflowService.deleteConnection(workflowId!, removedConnectionId);
        } catch (err: any) {
          if (!isNotFound(err)) throw err;
        }
      }
      setEdges(newEdges);
      setGraphDirty(false);
      setMetaDirty(false);
      removedNodeIdsRef.current.clear();
      removedConnectionIdsRef.current.clear();

      if (!id && workflowId) {
        navigate(`/app/workflows/${workflowId}`, { replace: true });
      }

      if (!silent) {
        enqueueSnackbar('Workflow saved successfully', { variant: 'success' });
      }
    } catch (err: any) {
      const data = err.response?.data;
      let msg = 'Failed to save workflow';
      if (data) {
        if (typeof data === 'string') {
          msg = data;
        } else if (data.error) {
          msg = data.error;
        } else if (data.detail) {
          msg = data.detail;
        } else {
          const fieldErrors = Object.entries(data)
            .map(([, v]) => Array.isArray(v) ? v.join(', ') : String(v))
            .join('; ');
          if (fieldErrors) msg = fieldErrors;
        }
      }
      setError(msg);
      if (!silent) {
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = async () => {
    if (canModify && (graphDirty || metaDirty)) {
      await handleSave({ silent: true });
    }
    navigate('/app/workflows');
  };

  const handleOpenCredentials = async () => {
    if (!canModify) return;
    if (!workflow.name || !String(workflow.name).trim()) {
      enqueueSnackbar('Please enter a workflow name first', { variant: 'warning' });
      return;
    }

    // Ensure workflow + latest graph/meta changes are persisted before opening credentials page.
    await handleSave({ silent: true });
    const workflowId = id || createdIdRef.current;
    if (!workflowId) {
      enqueueSnackbar('Please save workflow first', { variant: 'warning' });
      return;
    }
    navigate(`/app/workflows/${workflowId}/credentials`);
  };

  const handleTogglePublished = async () => {
    if (!id) {
      enqueueSnackbar('Save workflow first before publishing', { variant: 'warning' });
      return;
    }
    if (!canModify) {
      enqueueSnackbar('You do not have permission to edit workflows.', { variant: 'error' });
      return;
    }

    const nextPublished = !Boolean((workflow as any).is_published ?? true);
    try {
      setSaving(true);
      await workflowService.updateWorkflow(id, { is_published: nextPublished } as Partial<Workflow>);
      setWorkflow((prev) => ({ ...prev, is_published: nextPublished }));
      enqueueSnackbar(nextPublished ? 'Workflow published' : 'Workflow unpublished', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to update publish status', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleExecute = async () => {
    if (!id) {
      if (!canCreate) {
        enqueueSnackbar('You do not have permission to create workflows.', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Please save the workflow first', { variant: 'warning' });
      return;
    }

    try {
      if (!canExecute) {
        enqueueSnackbar('You do not have permission to execute workflows.', { variant: 'error' });
        return;
      }
      if (canModify) {
      await handleSave();
      }
      let triggerData: any = undefined;
      if (executeInputJson.trim()) {
        try {
          triggerData = JSON.parse(executeInputJson);
        } catch {
          enqueueSnackbar('Execute input must be valid JSON', { variant: 'error' });
          return;
        }
      }

      const result = await workflowService.executeWorkflow(id, triggerData);
      setExecutionId(result.execution_id);
      setExecution(null);
      setNodeExecutions([]);
      setExecutionPanelOpen(true);
      enqueueSnackbar('Execution started. See results panel on the right.', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to execute workflow', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!id) {
      navigate('/app/workflows');
      return;
    }

    try {
      if (!canDelete) {
        enqueueSnackbar('You do not have permission to delete workflows.', { variant: 'error' });
        return;
      }
      await workflowService.deleteWorkflow(id);
      enqueueSnackbar('Workflow deleted successfully', { variant: 'success' });
      navigate('/app/workflows');
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to delete workflow', { variant: 'error' });
    }
  };

  const handleExport = async () => {
    if (!id) {
      enqueueSnackbar('Save the workflow before exporting.', { variant: 'warning' });
      setMenuAnchor(null);
      return;
    }

    try {
      const data = await workflowService.exportWorkflow(id);
      const safeName = (workflow.name || 'workflow')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'workflow';
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `${safeName}-${stamp}.json`;

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      enqueueSnackbar(`Exported "${workflow.name || 'workflow'}"`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error || err.message || 'Failed to export workflow',
        { variant: 'error' }
      );
    } finally {
      setMenuAnchor(null);
    }
  };

  const handleImportClick = () => {
    if (!canCreate) {
      enqueueSnackbar('You do not have permission to create workflows.', { variant: 'error' });
      return;
    }
    importInputRef.current?.click();
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow importing the same file again
    e.target.value = '';
    if (!file) return;

    try {
      if (!canCreate) {
        enqueueSnackbar('You do not have permission to create workflows.', { variant: 'error' });
        return;
      }
      const text = await file.text();
      let json: any;
      try {
        json = JSON.parse(text);
      } catch (parseErr: any) {
        enqueueSnackbar(
          `Selected file is not valid JSON: ${parseErr?.message || parseErr}`,
          { variant: 'error' },
        );
        return;
      }
      const imported = await workflowService.importWorkflow(json);
      enqueueSnackbar('Workflow imported successfully', { variant: 'success' });
      navigate(`/app/workflows/${imported.id}`);
    } catch (err: any) {
      // The backend now returns a useful ``error`` field on 4xx. Prefer that
      // over the generic axios message ("Request failed with status code
      // 400") so users see "Workflow import failed: …".
      const backendError = err?.response?.data?.error;
      const backendDetail = err?.response?.data?.detail;
      const message =
        backendError ||
        backendDetail ||
        err?.message ||
        'Failed to import workflow';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <TextField
            variant="standard"
            placeholder="Workflow Name"
            value={workflow.name}
            onChange={(e) => {
              setWorkflow({ ...workflow, name: e.target.value });
              setMetaDirty(true);
            }}
            sx={{ flexGrow: 1, mr: 2 }}
            disabled={!canModify}
          />
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSave()}
            disabled={saving || !workflow.name || !canModify}
            sx={{ mr: 1 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<KeyIcon />}
            onClick={handleOpenCredentials}
            disabled={saving || !canModify}
            sx={{ mr: 1 }}
          >
            Credentials
          </Button>
          <Chip
            label={Boolean((workflow as any).is_published ?? true) ? 'Live' : 'Unpublished'}
            color={Boolean((workflow as any).is_published ?? true) ? 'success' : 'default'}
            sx={{ mr: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleTogglePublished}
            disabled={!id || saving || !canModify}
            sx={{ mr: 1 }}
          >
            {Boolean((workflow as any).is_published ?? true) ? 'Unpublish' : 'Publish'}
          </Button>
          {isNew && (
            <>
              <input
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
              <Button variant="outlined" onClick={handleImportClick} disabled={saving || !canCreate} sx={{ mr: 1 }}>
                Import JSON
              </Button>
            </>
          )}
          <Button
            variant={aiSidebarOpen ? 'contained' : 'outlined'}
            color="secondary"
            startIcon={<AiIcon />}
            onClick={() => setAiSidebarOpen((v) => !v)}
            sx={{ mr: 1 }}
          >
            AI Edit
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayIcon />}
            onClick={handleExecute}
            disabled={!id || saving || !canExecute}
            sx={{ mr: 1 }}
          >
            Execute
          </Button>
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {isSystemAdmin && (
              <MenuItem onClick={handleExport} disabled={!id}>
                <ExportIcon sx={{ mr: 1 }} /> Export JSON
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete} disabled={!canDelete}>
              <DeleteIcon sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid
          item
          xs={3}
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            minHeight: 0,
          }}
        >
          <NodePalette
            onNodeAdd={handleNodeAdd}
            onOpenLibrary={() => setLibraryOpen(true)}
            disabled={!canModify}
          />
          <LibraryDrawer
            open={libraryOpen}
            onClose={() => setLibraryOpen(false)}
            onNodeAdd={handleNodeAdd}
          />
        </Grid>
        <Grid item xs={aiSidebarOpen ? 6 : 9} sx={{ position: 'relative', transition: 'all 0.2s ease' }}>
          <ReactFlowProvider>
            <WorkflowCanvas
              nodes={nodes}
              edges={edges}
              onNodesChange={(changes: any) => {
                if (!canModify) return;
                if (saving || loading) return;
                if (changes?.length) {
                  for (const ch of changes) {
                    if (ch?.type === 'remove' && ch?.id && isUUID(ch.id)) {
                      removedNodeIdsRef.current.add(ch.id);
                    }
                  }
                  if (hasMeaningfulNodeChanges(changes)) {
                    setGraphDirty(true);
                  }
                }
                onNodesChange(changes);
              }}
              onEdgesChange={(changes: any) => {
                if (!canModify) return;
                if (saving || loading) return;
                if (changes?.length) {
                  for (const ch of changes) {
                    if (ch?.type === 'remove' && ch?.id && isUUID(ch.id)) {
                      removedConnectionIdsRef.current.add(ch.id);
                    }
                  }
                  if (hasMeaningfulEdgeChanges(changes)) {
                    setGraphDirty(true);
                  }
                }
                onEdgesChange(changes);
              }}
              onConnect={onConnect}
              onNodeAdd={handleNodeAdd}
              onNodeSelect={setSelectedNodeId}
              onEdgeSelect={setSelectedEdgeId}
              onNodeDragCommit={() => {
                if (!canModify) return;
                setGraphDirty(true);
              }}
            />
            <Panel position="top-right">
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              >
                <NodeInspector
                  node={selectedNode as any}
                  onChange={handleSelectedNodeChange}
                  onDelete={handleDeleteSelectedNode}
                  readOnly={!canModify}
                />
              </Box>
            </Panel>

            <Panel position="bottom-right">
              <Box
                sx={{
                  width: 420,
                  maxHeight: 320,
                  overflow: 'auto',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  boxShadow: 2,
                  p: 2,
                  display: executionPanelOpen ? 'block' : 'none',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Execution Results
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button size="small" variant="outlined" onClick={() => setExecutionModalOpen(true)}>
                      Expand
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setExecutionPanelOpen(false)}>
                      Close
                    </Button>
                  </Box>
                </Box>

                <TextField
                  label="Execute test input (JSON)"
                  fullWidth
                  multiline
                  minRows={5}
                  value={executeInputJson}
                  onChange={(e) => setExecuteInputJson(e.target.value)}
                  sx={{ mb: 1 }}
                  helperText='This becomes trigger_data (ex: {"body":{"question":"..."}}). Imported n8n prompts map to {{body.question}}.'
                />

                {!execution && (
                  <Typography variant="body2" color="text.secondary">
                    Loading execution…
                  </Typography>
                )}

                {execution && (
                  <>
                    <Typography variant="body2">
                      <strong>Status:</strong> {execution.status}
                    </Typography>
                    {execution.error_message && (
                      <Typography variant="body2" color="error" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {execution.error_message}
                      </Typography>
                    )}

                    <Box mt={2}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Node outputs
                      </Typography>
                      {nodeExecutions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No node outputs yet…
                        </Typography>
                      ) : (
                        <Box mt={1} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {nodeExecutions.map((ne) => (
                            <Box
                              key={ne.id}
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1,
                              }}
                            >
                              <Typography variant="body2" fontWeight="bold">
                                {ne.node_name} <span style={{ fontWeight: 400 }}>({ne.status})</span>
                              </Typography>
                              {ne.error_message && (
                                <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {ne.error_message}
                                </Typography>
                              )}
                              {ne.output_data && (
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                  {JSON.stringify((ne as any)?.output_data?.body ?? ne.output_data, null, 2)}
                                </pre>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Panel>

            <Dialog
              open={executionModalOpen}
              onClose={() => setExecutionModalOpen(false)}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: { width: '80vw', maxWidth: 1000, height: '80vh' },
              }}
            >
              <DialogTitle>Execution Results</DialogTitle>
              <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label="Execute test input (JSON)"
                  fullWidth
                  multiline
                  minRows={6}
                  value={executeInputJson}
                  onChange={(e) => setExecuteInputJson(e.target.value)}
                  helperText='This becomes trigger_data (ex: {"body":{"question":"..."}}). Imported n8n prompts map to {{body.question}}.'
                />

                {!execution && (
                  <Typography variant="body2" color="text.secondary">
                    Loading execution…
                  </Typography>
                )}

                {execution && (
                  <>
                    <Typography variant="body2">
                      <strong>Status:</strong> {execution.status}
                    </Typography>
                    {execution.error_message && (
                      <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                        {execution.error_message}
                      </Typography>
                    )}

                    <Box mt={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Node outputs
                      </Typography>
                      {nodeExecutions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No node outputs yet…
                        </Typography>
                      ) : (
                        <Box mt={1} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {nodeExecutions.map((ne) => (
                            <Box
                              key={ne.id}
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1,
                              }}
                            >
                              <Typography variant="body2" fontWeight="bold">
                                {ne.node_name} <span style={{ fontWeight: 400 }}>({ne.status})</span>
                              </Typography>
                              {ne.error_message && (
                                <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {ne.error_message}
                                </Typography>
                              )}
                              {ne.output_data && (
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                  {JSON.stringify((ne as any)?.output_data?.body ?? ne.output_data, null, 2)}
                                </pre>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setExecutionModalOpen(false)} variant="contained">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </ReactFlowProvider>
        </Grid>

        {aiSidebarOpen && (
          <Grid item xs={3} sx={{ height: '100%', overflow: 'hidden' }}>
            <WorkflowAIChat
              workflowId={id || createdIdRef.current || undefined}
              nodes={nodes}
              edges={edges}
              onApplyChanges={applyAIChanges}
              onUndo={handleAIUndo}
              onClose={() => setAiSidebarOpen(false)}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
